/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2018, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'./StaticWidget',
	'text!./Multipurpose.html',
	'./IconPlaceholderView',
	'./ShortcutPlaceholderView',
	'./TextWidget'
], function(_, StaticWidget, MultiPurposeTemplate, IconPlaceholderView, ShortcutPlaceholderView, TextWidget) {

	var MultipurposeWidget = StaticWidget.extend({
		init: function(options) {
			MultipurposeWidget.inherited('init', this, arguments);
			if (options && options.initialConfigJSON && options.initialConfigJSON.placeholder) {
				this.placeholder = options.initialConfigJSON.placeholder;
			}
		},

		onContainerReady: function () {
			MultipurposeWidget.inherited('onContainerReady', this, arguments);
			if (this.model) {
				if (this.model.fillColor) {
					this.$el.find('div.staticContent').addClass('fill-' + this.model.fillColor);
				}
				if (this.model.borderColor) {
					this.$el.find('div.staticContent').addClass('border-' + this.model.borderColor);
				}
			}
		},

		render: function() {
			this.$el.append(MultiPurposeTemplate);

			const viewOptions = { el: this.$el.find('.dropZone'), dashboardApi: this.dashboardApi };

			if (this.placeholder) {
				_.extend(viewOptions, this.placeholder);
				this.multiView = new IconPlaceholderView(viewOptions);
			} else {
				// shortcut callbacks
				_.extend(viewOptions, {
					onVisualizationSelected: () => {
						this.replaceWithVis();
					},
					onTextSelected: () => {
						this.replaceWithText();
					},
					onImageSelected: () => {
						this.replaceWithImage();
					}
				});
				this.multiView = new ShortcutPlaceholderView(viewOptions);
			}

			this.multiView.render();

			const DndManager = this.dashboardApi.getFeature('DashboardDnd.internal');
			DndManager.addDropTarget(this.$el, {
				accepts: (dragObject) => {
					if (this.placeholder) {
						return this._isDraggingData(dragObject);
					} else {
						return this._isDraggingData(dragObject) || this._isDraggingWidget(dragObject) || this._isMovingWidget(dragObject) || this._isDraggingPin(dragObject) || this._isDraggingGroup(dragObject);
					}
				},
				onDrop: (dragObject) => {
					this.$el.find('.dropZone').removeClass('active');
					return this._onDrop(dragObject);
				},
				onDragEnter: () => {
					this.$el.find('.dropZone').addClass('active');
				},
				onDragLeave: () => {
					this.$el.find('.dropZone').removeClass('active');
				}
			});
			return this.$el;
		},

		_isDraggingData: function (dragObject = {}) {
			const canvasDnD = this.dashboardApi.getFeature('CanvasDnD');
			return canvasDnD.accepts(dragObject, {
				fromMultipurposeWidget: true
			});
		},

		_isDraggingWidget: function (dragObject) {
			return dragObject.type === 'widget' && dragObject.data && dragObject.data.operation === 'new';
		},

		_isMovingWidget: function (dragObject) {
			// Only can move one widget to the placeholder at once
			return dragObject.type === 'widget' && dragObject.data && dragObject.data.nodeInfoList && dragObject.data.nodeInfoList.length === 1;
		},

		_isDraggingPin: function (dragObject) {
			return dragObject.type === 'pin';
		},

		_isDraggingGroup: function (dragObject) {
			return dragObject.type === 'groupContent';
		},

		destroy: function() {
			if( this.multiView && this.multiView.destroy){
				this.multiView.destroy();
			}

			if (this.model && this.model.id) {
				this.onChromeDeselected();
			}

			MultipurposeWidget.inherited('destroy', this, arguments);
		},

		registerEventGroup: function() {
			// Multipurpose widgets are not added to event groups
		},

		_onDrop: function(dragObject) {
			if (dragObject.data.operation === 'move') {
				return this._moveDrop(dragObject);
			} else if (this._isDraggingPin(dragObject) && dragObject.data.operation === 'new') {
				return this._onPinDrop(dragObject);
			} else if (dragObject.data.operation === 'new' || this._isDraggingData(dragObject)) {
				return this._newDrop(dragObject);
			}
		},

		_moveDrop: function(dragObject) {
			const transaction = this.dashboardApi.getFeature('Transaction');
			const canvas = this.dashboardApi.getCanvas();

			const transactionToken = transaction.startTransaction();
			const placeholderContent = canvas.getContent(this.id);
			let layout = this._getLayoutProperties(placeholderContent);

			canvas.removeContent(this.id, transactionToken);

			const nodeInfo = dragObject.data.nodeInfoList[0];
			const nodeModel = nodeInfo.node._layout.model;
			const content = canvas.getContent(nodeModel.id);

			this._setLayoutProperties(content, layout, transactionToken);
			// Need to move back to canvas as the widget drops to body by dnd manager
			canvas.moveContent(content.getContainer().getId(),[content.getId()], transactionToken);
			transaction.endTransaction(transactionToken);
		},

		_onPinDrop: function(dragObject) {
			const pinSpec = dragObject.data.pinSpec;
			return this._replaceSelf(pinSpec.content);
		},

		_newDrop: function(dragObject) {
			const canvasDnD = this.dashboardApi.getFeature('CanvasDnD');
			return canvasDnD.onDrop(dragObject)
				.then(newModel => {
					return this._replaceSelf(newModel);
				});
		},

		_replaceSelf: function(model) {
			const transaction = this.dashboardApi.getFeature('Transaction');
			const canvas = this.dashboardApi.getCanvas();

			const transactionToken = transaction.startTransaction();
			if (this.model) {
				if (this.model.fillColor) {
					model.fillColor = this.model.fillColor;
				}
				if (this.model.borderColor) {
					model.borderColor = this.model.borderColor;
				}
			}
			const placeholderContent = canvas.getContent(this.id);
			const content = {
				spec: model,
				containerId: this.content.getContainer().getId(),
				properties: this._getLayoutProperties(placeholderContent)
			};

			return canvas.addContent(content, transactionToken).then((newContent) => {
				const state = newContent.getFeature('state');
				return state.whenStatusChanges(state.STATUS.RENDERED)
					.then(() => {
						const selectedContentList = canvas.getSelectedContentList();
						const selectedContentIds = selectedContentList.map(item => item.getId());

						canvas.deselectContent(selectedContentIds);
						canvas.removeContent(this.id, transactionToken);
						canvas.selectContent([newContent.getId()]);

						transaction.endTransaction(transactionToken);
					});
			});
		},

		_getLayoutProperties: function (content) {
			return {
				height: content.getPropertyValue('height'),
				width: content.getPropertyValue('width'),
				left: content.getPropertyValue('left'),
				top: content.getPropertyValue('top')
			};
		},

		_setLayoutProperties: function (content, layout, transactionToken) {
			content.setPropertyValue('height', layout.height, transactionToken);
			content.setPropertyValue('width', layout.width, transactionToken);
			content.setPropertyValue('left', layout.left, transactionToken);
			content.setPropertyValue('top', layout.top, transactionToken);
		},

		replaceWithVis: function() {
			return this._replaceSelf({
				type: 'live',
				visId: 'com.ibm.vis.rave2bundlecolumn',
				expandOnRender: true,
				usePreferredSize: false
			});
		},

		replaceWithText: function() {
			const options = {
				style: 'body',
				placeholder: {
					showIcon: false
				}
			};
			return TextWidget.getDefaultSpec('', options).then((defaultSpec) => {
				return this._replaceSelf(defaultSpec.model);
			});
		},

		replaceWithImage: function() {
			return this._replaceSelf({ type: 'image' });
		},

		resize: function() {
			if(this.multiView.resize) {
				this.multiView.resize();
			}
		}
	});

	MultipurposeWidget.getDefaultSpec = () => {
		return Promise.resolve({
			model: {
				type: 'multipurpose',
				avatarHtml: MultiPurposeTemplate
			},
			layoutProperties: {
				style: {
					width: '240px',
					height: '180px'
				}
			}
		});
	};

	return MultipurposeWidget;
});
